/**
 * Shared draft shift types for API contracts between frontend and backend
 */
import { z } from "zod";
import { TipPoolingTypeSchema, ShiftResponseSchema } from "./shift.js";
// ===== INPUT SCHEMAS =====
/**
 * Create draft shift input schema
 * Note: editedBy is NOT included - routes inject it from req.user.userId
 * Note: companyId is optional for oRPC (comes from JWT) but required for Express routes
 */
export const CreateDraftShiftInputSchema = z.object({
    parentShiftId: z.uuid().nullable().optional(),
    startTime: z.iso.datetime(),
    endTime: z.iso.datetime(),
    jobRoleIds: z.array(z.uuid()).default([]),
    jobRoleFamilyId: z.uuid(),
    locationId: z.uuid(),
    employeeId: z.uuid().nullable().optional(),
    companyId: z.uuid().optional(), // Optional for oRPC (comes from JWT), included for Express compatibility
    tipPooling: TipPoolingTypeSchema.optional(),
    notes: z.string().max(1000).optional(),
});
/**
 * Update draft shift input schema
 * All fields are optional, but at least one must be provided
 */
export const UpdateDraftShiftInputSchema = z
    .object({
    startTime: z.iso.datetime().optional(),
    endTime: z.iso.datetime().optional(),
    jobRoleIds: z.array(z.uuid()).optional(),
    jobRoleFamilyId: z.uuid().optional(),
    locationId: z.uuid().optional(),
    employeeId: z.uuid().nullable().optional(),
    tipPooling: TipPoolingTypeSchema.optional(),
    notes: z.string().max(1000).optional(),
})
    .refine((data) => Object.keys(data).length > 0, {
    message: "At least one field must be provided",
});
/**
 * Publish drafts input schema
 * Takes an array of draft shift IDs to publish, with optional publish event tracking
 */
export const PublishDraftsInputSchema = z.object({
    draftIds: z.array(z.uuid()).min(1),
    // Optional publish event tracking (backward compatible)
    warningsSnapshot: z.record(z.string(), z.unknown()).optional(),
    overrideIds: z.array(z.uuid()).optional(),
    // When true, skip sending notifications to affected employees
    skipNotifications: z.boolean().optional().default(false),
});
/**
 * Revert drafts input schema
 * Takes an array of draft shift IDs to revert (delete)
 */
export const RevertDraftsInputSchema = z.object({
    draftIds: z.array(z.uuid()).min(1),
});
/**
 * Revert drafts by location input schema
 * Reverts all drafts for a location within a date range
 */
export const RevertDraftsByLocationInputSchema = z.object({
    locationId: z.uuid(),
    startDate: z.iso.datetime(),
    endDate: z.iso.datetime(),
});
/**
 * Get drafts query schema
 * Query parameters for fetching draft shifts
 */
export const GetDraftsQuerySchema = z.object({
    companyId: z.uuid().optional(), // Optional for oRPC (comes from JWT), included for Express compatibility
    startDate: z.iso.datetime().optional(),
    endDate: z.iso.datetime().optional(),
});
// ===== RESPONSE SCHEMAS (for oRPC migration) =====
/**
 * Validation warning schema
 */
export const ValidationWarningSchema = z.object({
    type: z.enum(["OVERLAPPING_SHIFT", "NO_PAY_CONFIGURATION", "INVALID_TIME_RANGE"]),
    message: z.string(),
    metadata: z.record(z.string(), z.unknown()).optional(),
});
/**
 * Draft shift result schema
 */
export const DraftShiftResultSchema = z.object({
    shift: ShiftResponseSchema,
    warnings: z.array(ValidationWarningSchema),
});
/**
 * Publish drafts response schema
 */
export const PublishDraftsResponseSchema = z.object({
    published: z.array(ShiftResponseSchema),
    publishEventId: z.uuid().optional(),
});
/**
 * Revert drafts response schema
 */
export const RevertDraftsResponseSchema = z.object({
    reverted: z.number(),
});
/**
 * Get draft for shift response schema
 */
export const GetDraftForShiftResponseSchema = z.object({
    draft: ShiftResponseSchema.nullable(),
});
/**
 * Get drafts response schema
 */
export const GetDraftsResponseSchema = z.object({
    drafts: z.array(ShiftResponseSchema),
});
